import { Send } from '@mui/icons-material'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import {
  Box,
  Button,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import type React from 'react'

import type { Activity } from '@/types/Activity'
import type { TrustedSourceRecommendation } from '@/types/Recommendation'
import { RequestType } from '@/types/Recommendation'

import RequestsList from './RequestsList'
import type { SubjectData } from './types'

interface Props {
  activeRequestType: RequestType
  setActiveRequestType: (type: RequestType) => void
  scrollRef: React.RefObject<HTMLDivElement>
  activeRecommendations: TrustedSourceRecommendation[]
  completeRecommendations: TrustedSourceRecommendation[]
  recommendationsCountByType: Record<RequestType, number>
  activeRequestActivity: Map<string, Activity[]>
  recommendationsLoading: boolean
  isFormSubmitting: boolean
  hasActive: boolean
  buttonText: string
  subjectData: SubjectData
  handleRequestsUpdated: () => void
  setViewMode: (mode: 'list' | 'form') => void
}

const RequestsListView: React.FC<Props> = ({
  activeRequestType,
  setActiveRequestType,
  scrollRef,
  activeRecommendations,
  completeRecommendations,
  recommendationsCountByType,
  activeRequestActivity,
  recommendationsLoading,
  isFormSubmitting,
  hasActive,
  buttonText,
  subjectData,
  handleRequestsUpdated,
  setViewMode,
}) => {
  const openBankingCount = recommendationsCountByType[RequestType.OPEN_BANKING]
  const payrollCount = recommendationsCountByType[RequestType.PAYROLL]

  return (
    <Box
      sx={{
        height: '100%',
        display: 'grid',
        gridTemplateRows: 'min-content 1fr min-content',
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <AccountBalanceIcon
              sx={{
                fontSize: 24,
                color: '#1B4554',
              }}
            />
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 600,
              }}
            >
              Trusted Sources Requests
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              letterSpacing: '0.14px',
              color: 'text.secondary',
            }}
          >
            Verify income and employment through secure third-party connections. Tenants
            link their bank or payroll provider directly.
          </Typography>
        </Box>

        <ToggleButtonGroup
          fullWidth
          value={activeRequestType}
          exclusive
          onChange={(event, newRequestType) => {
            if (newRequestType !== null) {
              setActiveRequestType(newRequestType)
            }
          }}
          sx={{ mt: 2 }}
        >
          <ToggleButton
            value={RequestType.OPEN_BANKING}
            sx={[
              {
                px: 3,
                py: 1,
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '8px 0 0 8px',
                border: '1px solid #1B4554',
                '&.Mui-selected': {
                  bgcolor: '#1B4554',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#12303A',
                  },
                },
              },
              activeRequestType === RequestType.OPEN_BANKING
                ? {
                    bgcolor: '#1B4554',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#12303A',
                    },
                  }
                : {
                    bgcolor: 'white',
                    color: '#1B4554',
                    '&:hover': {
                      bgcolor: 'rgba(27, 69, 84, 0.06)',
                    },
                  },
            ]}
          >
            Open Banking ({openBankingCount})
          </ToggleButton>
          <ToggleButton
            value={RequestType.PAYROLL}
            sx={[
              {
                px: 3,
                py: 1,
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '0 8px 8px 0',
                border: '1px solid #1B4554',
                borderLeft: '0',
                '&.Mui-selected': {
                  bgcolor: '#1B4554',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#12303A',
                  },
                },
              },
              activeRequestType === RequestType.PAYROLL
                ? {
                    bgcolor: '#1B4554',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#12303A',
                    },
                  }
                : {
                    bgcolor: 'white',
                    color: '#1B4554',
                    '&:hover': {
                      bgcolor: 'rgba(27, 69, 84, 0.06)',
                    },
                  },
            ]}
          >
            Payroll/HMRC ({payrollCount})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box ref={scrollRef} sx={{ overflow: 'auto', px: 3 }}>
        <Stack spacing={3}>
          <RequestsList
            title="Active Requests"
            recommendations={activeRecommendations}
            activitiesByRecommendation={activeRequestActivity}
            type="active"
            emptyMessage="There's no active requests for this applicant."
            subjectData={subjectData}
            onRequestChanged={handleRequestsUpdated}
          />

          <RequestsList
            title="Completed Requests"
            recommendations={completeRecommendations}
            activitiesByRecommendation={activeRequestActivity}
            type="completed"
            emptyMessage="There are no completed requests."
            subjectData={subjectData}
          />
        </Stack>
      </Box>
      <Box
        sx={{
          p: 3,
          borderTop: '1px solid #f2f2f2',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          startIcon={<Send />}
          loading={recommendationsLoading || isFormSubmitting}
          disabled={recommendationsLoading || isFormSubmitting || hasActive}
          onClick={() => setViewMode('form')}
          sx={{
            bgcolor: '#1B4554',
            color: 'white',
            py: 1.5,
            fontSize: '14px',
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: '8px',
            '&:hover': {
              bgcolor: '#12303A',
            },
          }}
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  )
}

export default RequestsListView
